/* eslint-disable react/prop-types, prettier/prettier */
import { keyframes } from '@emotion/react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SendIcon from '@mui/icons-material/Send'
import TableChartIcon from '@mui/icons-material/TableChart'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, CircularProgress, TextField, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AIService from '../../services/aiService'
import useAiStatus from '../../hooks/useAiStatus'
import AiStatusGate from '../../components/shared/AiStatusGate'
import GlowButton from '../../components/shared/GlowButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { parseCSV } from '../../utils/csv'
import {
    ACCENT,
    BRAND,
    CATEGORY,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

/* ── animations ──────────────────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`
const shimmerSweep = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`
const slideIn = keyframes`
  from { opacity:0; transform:translateX(20px); }
  to   { opacity:1; transform:translateX(0); }
`

/* ── palette (page accents derived from tokens) ──────────────────────────── */
const C = {
    border: SURFACE.border,
    accent: BRAND.orange,
    // Column names: the categorical cyan (text-safe on its tint).
    sky: CATEGORY.cyan,
    text: TEXT.primary,
    textDim: TEXT.muted,
    textMuted: TEXT.faint,
    green: STATUS.success.fg,
    yellow: STATUS.warning.fg,
    red: STATUS.error.fg,
}

// The cards are the brand's Frames (white on light, Deep Blue on dark).
const GLASS = cardSx

const MONO_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        typography: 'body2',
        fontFamily: FONT.mono,
    },
}

const LABEL = {
    typography: 'overline',
    display: 'block',
    color: C.textMuted,
    mb: 0.75,
}

const SAMPLE_PLACEHOLDER =
    'mobile_no,name,batch_id,join_link\n+919876543210,Aryan Sharma,B001,https://meet.bhanzu.com/b001\n+918765432109,Priya Nair,B002,https://meet.bhanzu.com/b002\n+917654321098,Rohan Mehta,B001,https://meet.bhanzu.com/b001\n+916543210987,Ananya Singh,B003,https://meet.bhanzu.com/b003'

/* ── CSV parsing (shared RFC 4180 parser, adapted to this page's shape) ──── */
function parseCsvInput(text) {
    const { headers, rows } = parseCSV(text)
    const columns = headers.filter(Boolean)
    if (columns.length === 0 || rows.length === 0) return null
    return { columns, rowCount: rows.length, rows }
}

/* ── confidence colour ───────────────────────────────────────────────────── */
export function confStyle(conf) {
    const tone =
        conf >= 0.8
            ? STATUS.success
            : conf >= 0.5
            ? STATUS.warning
            : STATUS.error
    return { bg: tone.bg, border: tone.border, text: tone.fg }
}

/* ── ShimmerBlock ────────────────────────────────────────────────────────── */
const ShimmerBlock = ({ width = '100%', height = 16 }) => (
    <Box
        sx={{
            width,
            height,
            borderRadius: `${RADIUS.xs}px`,
            background: `linear-gradient(90deg, ${ACCENT.bg04} 25%, ${ACCENT.bg15} 50%, ${ACCENT.bg04} 75%)`,
            backgroundSize: '200% 100%',
            animation: `${shimmerSweep} 1.6s ease-in-out infinite`,
        }}
    />
)

/* ── main component ──────────────────────────────────────────────────────── */
const CsvValidator = () => {
    const ai = useAiStatus()
    const navigate = useNavigate()
    const resultsRef = useRef(null)

    const [csvText, setCsvText] = useState('')
    const [parsed, setParsed] = useState(null)
    const [validating, setValidating] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        if (result && resultsRef.current) {
            setTimeout(
                () =>
                    resultsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    }),
                100
            )
        }
    }, [result])

    const handleTextChange = (text) => {
        setCsvText(text)
        setResult(null)
        setError('')
        setParsed(parseCsvInput(text))
    }

    const handleValidate = async () => {
        if (!parsed) return
        setValidating(true)
        setError('')
        setResult(null)
        try {
            const res = await AIService.validateCSV({
                columns: parsed.columns,
                sample_rows: parsed.rows.slice(0, 5),
                total_rows: parsed.rows.length,
            })
            setResult(res.data?.result ?? null)
        } catch {
            setError('Validation failed. Please try again.')
        } finally {
            setValidating(false)
        }
    }

    // Checking, no key, or the check failed: one shared screen.
    if (ai.state !== 'ready') return <AiStatusGate status={ai} />

    /* ── main layout ── */
    return (
        <Box
            sx={{
                ...pageSx,
                backgroundImage:
                    `radial-gradient(ellipse at 15% 30%, ${tint(
                        BRAND.orange,
                        0.1
                    )} 0%, transparent 50%),` +
                    `radial-gradient(ellipse at 85% 70%, ${tint(
                        CATEGORY.cyan,
                        0.06
                    )} 0%, transparent 45%),` +
                    `radial-gradient(${tint(
                        BRAND.orange,
                        0.07
                    )} 1px, transparent 1px)`,
                backgroundSize: 'auto, auto, 28px 28px',
            }}
        >
            <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* ── page heading ── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1,
                        animation: `${fadeUp} 0.3s ease`,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: `${RADIUS.md}px`,
                            background: GRADIENT.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 0 16px ${ACCENT.border30}`,
                        }}
                    >
                        {/* Deep Blue on the brand gradient (white was 1.8:1 on its yellow). */}
                        <TableChartIcon
                            sx={{ fontSize: ICON.lg, color: ON_ORANGE }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ color: C.text, lineHeight: 1.2 }}
                        >
                            CSV Validator
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: C.textMuted, mt: 0.25 }}
                        >
                            Paste your CSV — AI maps columns and flags data
                            issues before bulk send.
                        </Typography>
                    </Box>
                </Box>

                {/* ── input card ── */}
                <Box
                    sx={{
                        ...GLASS,
                        p: 3,
                        mb: 2.5,
                        animation: `${fadeUp} 0.35s ease`,
                    }}
                >
                    <Typography sx={LABEL}>CSV Data</Typography>
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: C.textMuted, mb: 1.5 }}
                    >
                        Paste with headers in the first row. Up to 5 sample rows
                        are sent for analysis.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={14}
                        placeholder={SAMPLE_PLACEHOLDER}
                        value={csvText}
                        onChange={(e) => handleTextChange(e.target.value)}
                        sx={MONO_INPUT_SX}
                    />

                    {/* ── parsed preview ── */}
                    {parsed && (
                        <Box sx={{ mt: 2, animation: `${fadeUp} 0.25s ease` }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                <CheckCircleIcon
                                    sx={{ fontSize: ICON.sm, color: C.green }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ color: C.green, fontWeight: 600 }}
                                >
                                    {parsed.columns.length} column
                                    {parsed.columns.length !== 1
                                        ? 's'
                                        : ''} · {parsed.rowCount} data row
                                    {parsed.rowCount !== 1 ? 's' : ''} detected
                                </Typography>
                            </Box>
                            {/* column chips */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.75,
                                    mb: 2.5,
                                }}
                            >
                                {parsed.columns.map((col) => (
                                    <Box
                                        key={col}
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: `${RADIUS.xs}px`,
                                            background: tint(C.sky, 0.08),
                                            border: `1px solid ${tint(
                                                C.sky,
                                                0.25
                                            )}`,
                                            color: C.sky,
                                            typography: 'caption',
                                            fontFamily: FONT.mono,
                                        }}
                                    >
                                        {col}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <GlowButton
                        loading={validating}
                        disabled={!parsed}
                        onClick={handleValidate}
                    >
                        Validate CSV
                    </GlowButton>

                    {error && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={error} />
                        </Box>
                    )}
                </Box>

                {/* ── loading skeleton ── */}
                {validating && (
                    <Box
                        sx={{
                            ...GLASS,
                            p: 3,
                            animation: `${fadeUp} 0.3s ease`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2.5,
                            }}
                        >
                            <CircularProgress
                                size={14}
                                sx={{ color: C.accent }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: C.textDim }}
                            >
                                Analysing columns and data…
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            <ShimmerBlock width="55%" />
                            <ShimmerBlock width="80%" />
                            <ShimmerBlock width="65%" />
                            <ShimmerBlock width="40%" height={12} />
                        </Box>
                    </Box>
                )}

                {/* ── results card ── */}
                {result && !validating && (
                    <Box
                        ref={resultsRef}
                        sx={{
                            ...GLASS,
                            overflow: 'hidden',
                            animation: `${slideIn} 0.4s ease`,
                        }}
                    >
                        {/* result header */}
                        <Box
                            sx={{
                                px: 3,
                                py: 1.75,
                                borderBottom: `1px solid ${C.border}`,
                                background: ACCENT.bg04,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    background: result.issues?.length
                                        ? C.yellow
                                        : C.green,
                                    // A soft halo in the dot's own tint (a neon glow did
                                    // not read on the light theme).
                                    boxShadow: `0 0 0 3px ${
                                        result.issues?.length
                                            ? STATUS.warning.bg
                                            : STATUS.success.bg
                                    }`,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: C.text }}
                            >
                                {result.issues?.length
                                    ? 'Issues found'
                                    : 'Looks good'}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            {/* summary */}
                            {result.summary && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: C.textDim,
                                        mb: 3,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {result.summary}
                                </Typography>
                            )}

                            {/* ── mappings ── */}
                            {result.mappings?.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={LABEL}>
                                        Column Mappings
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: `1px solid ${C.border}`,
                                            borderRadius: `${RADIUS.md}px`,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {result.mappings.map((m, i) => {
                                            const cs = confStyle(m.confidence)
                                            return (
                                                <Box
                                                    key={m.column}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 1.25,
                                                        gap: 2,
                                                        borderBottom:
                                                            i <
                                                            result.mappings
                                                                .length -
                                                                1
                                                                ? `1px solid ${C.border}`
                                                                : 'none',
                                                        background:
                                                            i % 2 === 0
                                                                ? SURFACE.subtle
                                                                : 'transparent',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontFamily:
                                                                FONT.mono,
                                                            color: C.sky,
                                                            minWidth: 140,
                                                        }}
                                                    >
                                                        {m.column}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: C.textMuted,
                                                            mx: 0.5,
                                                        }}
                                                    >
                                                        →
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: C.text,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {m.maps_to}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            px: 1,
                                                            py: 0.2,
                                                            borderRadius: `${RADIUS.xs}px`,
                                                            background: cs.bg,
                                                            border: `1px solid ${cs.border}`,
                                                            color: cs.text,
                                                            typography:
                                                                'caption',
                                                            fontWeight: 600,
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        {Math.round(
                                                            m.confidence * 100
                                                        )}
                                                        % match
                                                    </Box>
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {/* ── issues ── */}
                            {result.issues?.length > 0 ? (
                                <Box>
                                    <Typography sx={LABEL}>Issues</Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        {result.issues.map((issue, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    alignItems: 'flex-start',
                                                    p: 1.5,
                                                    borderRadius: `${RADIUS.sm}px`,
                                                    background: STATUS.error.bg,
                                                    border: `1px solid ${STATUS.error.border}`,
                                                }}
                                            >
                                                <WarningAmberIcon
                                                    sx={{
                                                        fontSize: ICON.md,
                                                        color: C.yellow,
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 1,
                                                            mb: 0.25,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontFamily:
                                                                    FONT.mono,
                                                                color: C.sky,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {issue.column}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        component="p"
                                                        sx={{
                                                            color: C.textDim,
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {issue.issue}
                                                    </Typography>
                                                    {issue.example_bad_value && (
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    'inline-flex',
                                                                mt: 0.75,
                                                                px: 1,
                                                                py: 0.2,
                                                                borderRadius: `${RADIUS.xs}px`,
                                                                background:
                                                                    SURFACE.card,
                                                                border: `1px solid ${STATUS.error.border}`,
                                                                color: C.red,
                                                                typography:
                                                                    'caption',
                                                                fontFamily:
                                                                    FONT.mono,
                                                            }}
                                                        >
                                                            {
                                                                issue.example_bad_value
                                                            }
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: `${RADIUS.sm}px`,
                                        background: STATUS.success.bg,
                                        border: `1px solid ${STATUS.success.border}`,
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: C.green,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: C.green, fontWeight: 500 }}
                                    >
                                        No issues found — CSV looks ready for
                                        bulk send.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* ── post-result CTAs ── */}
                {result && !validating && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <GlowButton
                            startIcon={<SendIcon sx={{ fontSize: ICON.md }} />}
                            onClick={() =>
                                navigate('/bulk-send', {
                                    state: { csvText: csvText },
                                })
                            }
                        >
                            Proceed to Bulk Send
                        </GlowButton>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default CsvValidator
